import { debounce } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';

import { useFocusOnCtrlK } from 'src/utils/use-focus-on-ctrl-k';
import Search from 'src/components/search/Search';
import { isMac, isMobileUser } from 'src/common/utils';

const HOTKEY = isMac() ? '⌘K' : 'Ctrl+K';

type Props = {
    searchScope: 'inbox' | 'all';
    search: string;
    setSearch: (newSearch: string) => void;
}

const getDesktopPlaceholder = (scope: 'inbox' | 'all') => {
    if (scope === 'inbox') {
        return 'Search this inbox by lead name, email, phone, UUID, notes';
    } else {
        return 'Search all conversations by message content, lead name, email, phone number';
    }
};

export default function SearchInput({ searchScope, search, setSearch }: Props) {
    const { width: windowWidth } = useWindowSize();

    const inputRef = useRef<HTMLInputElement>(null);

    const [value, setValue] = useState(search);
    const [isSearching, setSearching] = useState(false);

    const setSearchQueryDebounced = useCallback(debounce(setSearch, 300), []);

    useEffect(() => {
        setSearchQueryDebounced(value);
    }, [value, setSearchQueryDebounced]);

    useEffect(() => {
        if (value !== search) {
            setSearching(true);
        } else {
            setSearching(false);
        }
    }, [value, search]);

    useFocusOnCtrlK(inputRef as React.RefObject<HTMLInputElement>, 'Inbox');

    return (
        <Search
            inputRef={inputRef}
            placeholder={isMobileUser() ? 'Search conversations' : getDesktopPlaceholder(searchScope)}
            value={value}
            onChange={setValue}
            isLoading={isSearching}
            sx={{ width: windowWidth >= 1280 ? 700 : 400, maxWidth: '100%', minWidth: '30%', bgcolor: 'common.white' }}
            data-testid="conversations-search"
            hotkey={HOTKEY}
        />
    );
}
